import { useContext } from 'react';
import {
  Form,
  FieldSet,
  Button,
  ColumnLayout,
  Column,
  Typography,
  ModalBody,
} from '@visionplanner/ui-react-material';
import PropTypes from 'prop-types';
import isEqual from 'lodash.isequal';
import { firmKeyDetailsFormSchema } from './partner-form-validation';
import { StyledModalFooter } from '../../styles';
import { saveFirmKeyDetails } from '../partner-onboarding.requests';
import { PartnerContext } from '../partner-context';
import { types } from '../partner-onboarding.reducer';
import { useAsyncCallback } from '../../../common/custom-hooks/use-async-callback';
import useInternationalizationConfiguration from '../../../common/custom-hooks/use-internationalization-configuration';
import { region } from '../../../common/enums';

/** Component to update firm-key-details step details */

const FirmKeyDetails = ({ validations, sbiOptions }) => {
  const {
    activeStep,
    setActiveStep,
    state,
    dispatch,
  } = useContext(PartnerContext);
  const { firmKeyDetails, partnerId } = state;

  const { isRegion } = useInternationalizationConfiguration();
  const isBERegion = isRegion(region.BE);
  const firmKeyDetailsFormRegionSchema = validations && firmKeyDetailsFormSchema({ cocNumberRegex: validations.cocNumberRegex, cocNumberMessage: validations.cocNumberMessage });

  const handleSubmit = useAsyncCallback(async (formValues) => {
    if (!isEqual(firmKeyDetails, formValues)) {
      await saveFirmKeyDetails(partnerId, formValues);
      dispatch({
        type: types.savefirmKeyDetails,
        payload: formValues,
      });
    }
    setActiveStep(activeStep + 1);
  }, [activeStep, dispatch, firmKeyDetails, partnerId, setActiveStep]);

  return (
    <>
      <Form
        initialValues={firmKeyDetails}
        validateFormSchema={firmKeyDetailsFormRegionSchema}
        onSubmit={handleSubmit}
        dataTa="firm-key-details-form"
        isInitiallyValid={firmKeyDetailsFormRegionSchema?.isValidSync(firmKeyDetails)}
        className="full-height flex-column"
      >
        {
          ({
            isValid, isSubmitting,
          }) => (
            <>
              <ModalBody>
                <Typography use="h6">Basis informatie kantoor</Typography>
                <ColumnLayout>
                  <Column span={5}>
                    <FieldSet
                      name="kvkNumber"
                      label={isBERegion ? 'Ondernemingsnummer' : 'Kvk-nummer'}
                      dataTa="kvk-number"
                      required
                    />
                  </Column>
                </ColumnLayout>
                <ColumnLayout>
                  <Column span={8}>
                    <FieldSet
                      type="text"
                      name="companyName"
                      label="Bedrijfsnaam"
                      dataTa="company-name"
                      required
                    />
                  </Column>
                </ColumnLayout>
                <ColumnLayout>
                  <Column span={7}>
                    <FieldSet
                      type="selectOne"
                      controlType="autocomplete"
                      options={sbiOptions}
                      name="standardCompanyClassification"
                      label="Standaard bedrijfsindeling"
                      placeholder="Selecteer klassificatie"
                      dataTa="standard-classification"
                      required
                      noOptionsMessage="Geen opties"
                      className="standard-classification"
                    />
                  </Column>
                </ColumnLayout>
                <ColumnLayout>
                  <Column span={2}>
                    <FieldSet
                      type="number"
                      name="noOfEmployees"
                      label="Aantal medewerkers"
                      required
                      dataTa="number-of-employees"
                    />
                  </Column>
                </ColumnLayout>
                <ColumnLayout>
                  <Column span={2}>
                    <FieldSet
                      type="number"
                      name="noOfClients"
                      label="Aantal klanten"
                      required
                      dataTa="number-of-clients"
                    />
                  </Column>
                </ColumnLayout>
              </ModalBody>
              <StyledModalFooter>
                <Button type="submit" dataTa="go-to-firm-address" disabled={!isValid || isSubmitting}>Volgende</Button>
              </StyledModalFooter>
            </>
          )
        }
      </Form>
    </>
  );
};

FirmKeyDetails.propTypes = {
  /** List of sbi options */
  sbiOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
};

export default FirmKeyDetails;
